import { formatDateString } from "src/utils/format-date"

const formatDate = (date) => {
    if (!date) return "-"

    return formatDateString(date, "-")
}

const LOCKUP_KEYS = ["IG", "IP", "IQ"]

const getLockupRows = (offerData) => {
    const datasLockup = offerData?.DatasLockup ?? {}

    return LOCKUP_KEYS.map((origem) => ({
        label: origem,
        values: LOCKUP_KEYS.map((destino) => ({
            label: `Data lockup ${origem} para ${destino}`,
            value: formatDate(datasLockup?.[origem]?.[destino]),
        })),
    }))
}

const hasOfferDetails = (offerData) => {
    return [true, 1, "1", "true", "True"].includes(
        offerData?.PossuiDadosOferta
    )
}

const normalizeText = (value) => {
    return String(value ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
}

const isPrivateOffer = (tipoOferta) => {
    return normalizeText(tipoOferta).includes("privada")
}

const shouldShowEmptyOfferModal = (offerData) => {
    return (
        !offerData?.TipoOferta ||
        isPrivateOffer(offerData?.TipoOferta) ||
        !hasOfferDetails(offerData)
    )
}

const getOfferModalTitle = (offerData) => {
    if (!offerData?.TipoOferta) {
        return "Tipo de Oferta"
    }

    if (isPrivateOffer(offerData.TipoOferta)) {
        return "Oferta Privada"
    }

    return "Oferta Pública de Distribuição"
}

const formatCPFCNPJ = (value) => {
    if (!value) return "-"

    const digits = value.replace(/\D/g, "")

    if (digits.length === 8) {
        return digits.replace(/(\d{2})(\d{3})(\d{3})/, "$1.$2.$3")
    }

    if (digits.length === 11) {
        return digits.replace(
            /(\d{3})(\d{3})(\d{3})(\d{2})/,
            "$1.$2.$3-$4"
        )
    }

    if (digits.length === 14) {
        return digits.replace(
            /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
            "$1.$2.$3/$4-$5"
        )
    }

    return value
}

const DEFAULT_COORDENADORES = [
    "Coordenador líder",
    "Coordenador 1",
    "Coordenador 2",
    "Coordenador 3",
    "Coordenador 4",
    "Coordenador 5",
    "Coordenador 6",
    "Coordenador 7",
]

const getCoordenadoresCards = (offerData) => {
    const coordenadores = offerData?.Coordenadores ?? []

    return DEFAULT_COORDENADORES.map((tipo, index) => {
        const coordenador =
            coordenadores.find((item) => item?.Tipo === tipo) ??
            coordenadores[index]

        return {
            title: tipo,
            value: coordenador?.Nome ?? "-",
            optionalText: formatCPFCNPJ(coordenador?.CPFCNPJ),
        }
    })
}

const getOfertaModalSections = (offerData) => [
    {
        type: "split",
        gridTemplateColumns: {
            xxxs: "1fr",
            xs: "1fr",
            sm: "1fr",
            md: "1fr",
            lg: "minmax(0, 9fr) minmax(0, 3fr)",
        },
        columns: [
            {
                title: "SRE CVM",
                md: 9,
                fields: [
                    {
                        label: "Tipo rito",
                        value: offerData?.SRECVM?.TipoRito,
                        xs: 6,
                        sm: 6,
                        md: 2,
                    },
                    {
                        label: "Data requerimento",
                        value: formatDate(offerData?.SRECVM?.DataRequerimento),
                        xs: 6,
                        sm: 6,
                        md: 2,
                    },
                    {
                        label: "Distribuição",
                        value: offerData?.SRECVM?.Distribuicao,
                        xs: 6,
                        sm: 6,
                        md: 2,
                    },
                    {
                        label: "Link SRE",
                        value: offerData?.SRECVM?.LinkSRE,
                        href: offerData?.SRECVM?.LinkSRE,
                        xs: 6,
                        sm: 6,
                        md: 2,
                    },
                    {
                        label: "Tipo mercado",
                        value: offerData?.SRECVM?.TipoMercado,
                        xs: 6,
                        sm: 6,
                        md: 2,
                    },
                    {
                        label: "Regime distribuição",
                        value: offerData?.SRECVM?.RegimeDistribuicao,
                        xs: 6,
                        sm: 6,
                        md: 2,
                    },
                ],
            },
            {
                title: "Data Reserva",
                md: 3,
                fields: [
                    {
                        label: "Início",
                        value: formatDate(offerData?.DataReserva?.Inicio),
                        xs: 6,
                        sm: 6,
                        md: 6,
                    },
                    {
                        label: "Encerramento",
                        value: formatDate(offerData?.DataReserva?.Encerramento),
                        xs: 6,
                        sm: 6,
                        md: 6,
                    },
                ],
            },
        ],
    },
    {
        type: "split",
        mb: 1,
        columns: [
            {
                title: "Data Oferta",
                md: 12,
                fields: [
                    {
                        label: "Início",
                        value: formatDate(offerData?.DataOferta?.Inicio),
                        xs: 6,
                        sm: 6,
                        md: 2,
                    },
                    {
                        label: "Encerramento máximo",
                        value: formatDate(offerData?.DataOferta?.EncerramentoMaximo),
                        xs: 6,
                        sm: 6,
                        md: 2,
                    },
                    {
                        label: "Encerramento efetivo",
                        value: formatDate(offerData?.DataOferta?.EncerramentoEfetivo),
                        xs: 6,
                        sm: 6,
                        md: 2,
                    },
                ],
            },
        ],
    },
    {
        type: "split",
        mb: 2,
        columns: [
            {
                title: "Bookbuilding",
                md: 12,
                fields: [
                    {
                        label: "Tem",
                        value: offerData?.Bookbuilding?.Tem,
                        xs: 6,
                        sm: 6,
                        md: 2,
                    },
                    {
                        label: "Data",
                        value: formatDate(offerData?.Bookbuilding?.Data),
                        xs: 6,
                        sm: 6,
                        md: 2,
                    },
                    {
                        label: "Remuneração",
                        value: offerData?.Bookbuilding?.Remuneracao,
                        xs: 6,
                        sm: 6,
                        md: 3,
                    },
                ],
            },
        ],
    },
    {
        type: "matrix",
        title: "Publico Alvo e Datas Lockup",
        mb: 0.5,
        columns: LOCKUP_KEYS,
        rows: getLockupRows(offerData),
        sideGroups: [
            {
                title: "Publico alvo investidor geral",
                fields: [
                    {
                        label: "Inicial",
                        value: offerData?.PublicoAlvo?.InvestidorGeral?.Inicial,
                        xs: 6,
                        sm: 6,
                        md: 2,
                    },
                    {
                        label: "Final",
                        value: offerData?.PublicoAlvo?.InvestidorGeral?.Final,
                        xs: 6,
                        sm: 6,
                        md: 2,
                    },
                ],
            },
            {
                title: "Publico alvo investidor qualificado",
                fields: [
                    {
                        label: "Inicial",
                        value: offerData?.PublicoAlvo?.InvestidorQualificado
                            ?.Inicial,
                        xs: 6,
                        sm: 6,
                        md: 2,
                    },
                    {
                        label: "Final",
                        value: offerData?.PublicoAlvo?.InvestidorQualificado
                            ?.Final,
                        xs: 6,
                        sm: 6,
                        md: 2,
                    },
                ],
            },
            {
                title: "Publico alvo investidor profissional",
                fields: [
                    {
                        label: "Inicial",
                        value: offerData?.PublicoAlvo?.InvestidorProfissional
                            ?.Inicial,
                        xs: 6,
                        sm: 6,
                        md: 2,
                    },
                    {
                        label: "Final",
                        value: offerData?.PublicoAlvo?.InvestidorProfissional
                            ?.Final,
                        xs: 6,
                        sm: 6,
                        md: 2,
                    },
                ],
            },
        ],
    },
    {
        type: "cards",
        title: "Coordenadores",
        titleSx: {
            mb: 0.5,
        },
        cards: getCoordenadoresCards(offerData),
    },
]

const getGarantiasModalSections = (garantiasData) => [
    {
        title: "Garantias Reais",
        fields: [
            {
                label: `ALIENAÇÃO FIDUCIÁRIA: ${garantiasData?.GarantiasReais?.ValorAlienacaoFiduciaria ?? ""
                    }`,
                value:
                    garantiasData?.GarantiasReais?.AlienacaoFiduciaria ??
                    "-",
            },
            {
                label: `CESSÃO FIDUCIÁRIA: ${garantiasData?.GarantiasReais?.ValorCessaoFiduciaria ?? ""
                    }`,
                value:
                    garantiasData?.GarantiasReais?.CessaoFiduciaria ??
                    "-",
            },
            {
                label: `HIPOTECA: ${garantiasData?.GarantiasReais?.ValorHipoteca ?? ""
                    }`,
                value:
                    garantiasData?.GarantiasReais?.Hipoteca ??
                    "-",
            },
            {
                label: `PENHOR: ${garantiasData?.GarantiasReais?.ValorPenhor ?? ""
                    }`,
                value:
                    garantiasData?.GarantiasReais?.Penhor ??
                    "-",
            },
        ],
    },

    {
        title: "Garantias Fidejussórias",
        fields: [
            {
                label: `AVAL: ${garantiasData?.GarantiasFidejussorias?.ValorAval ?? ""
                    }`,
                value:
                    garantiasData?.GarantiasFidejussorias?.Aval ??
                    "-",
            },
            {
                label: `FIANÇA: ${garantiasData?.GarantiasFidejussorias?.ValorFianca ?? ""
                    }`,
                value:
                    garantiasData?.GarantiasFidejussorias?.Fianca ??
                    "-",
            },
            {
                label: `FIANÇA BANCÁRIA: ${garantiasData?.GarantiasFidejussorias?.ValorFiancaBancaria ?? ""
                    }`,
                value:
                    garantiasData?.GarantiasFidejussorias?.FiancaBancaria ??
                    "-",
            },
        ],
    },

    {
        title: "Mitigador de risco de crédito",
        fields: [
            {
                label: `FUNDO OBRA: ${garantiasData?.Mitigadores?.ValorFundoObra ?? ""
                    }`,
                value:
                    garantiasData?.Mitigadores?.FundoObra ??
                    "-",
            },
            {
                label: `FUNDO RESERVA: ${garantiasData?.Mitigadores?.ValorFundoReserva ?? ""
                    }`,
                value:
                    garantiasData?.Mitigadores?.FundoReserva ??
                    "-",
            },
            {
                label: `FUNDO LIQUIDEZ: ${garantiasData?.Mitigadores?.ValorFundoLiquidez ?? ""
                    }`,
                value:
                    garantiasData?.Mitigadores?.FundoLiquidez ??
                    "-",
            },
            {
                label: `SOBREGARANTIA: ${garantiasData?.Mitigadores?.ValorSobregarantia ?? ""
                    }`,
                value:
                    garantiasData?.Mitigadores?.Sobregarantia ??
                    "-",
            },
            {
                label: `SEGURO: ${garantiasData?.Mitigadores?.ValorSeguro ?? ""
                    }`,
                value:
                    garantiasData?.Mitigadores?.Seguro ??
                    "-",
            },
            {
                label: `COTAS INFERIORES: ${garantiasData?.Mitigadores?.ValorCotasInferiores ?? ""
                    }`,
                value:
                    garantiasData?.Mitigadores?.CotasInferiores ??
                    "-",
            },
        ],
    },

    {
        title: "LTV",
        fields: [
            {
                label: "DATA REFERÊNCIA",
                value:
                    formatDate(garantiasData?.LTV?.DataReferencia) ??
                    "-",
            },
            {
                label: "VALOR",
                value:
                    garantiasData?.LTV?.LTV ??
                    "-",
            },
            {
                label: "CETIP",
                value:
                    garantiasData?.LTV?.CETIP ??
                    "-",
            },
        ],
    },
]
const getFatosRelevantesModalSections = (fatosRelevantesData = [], characterData = {}) => {
    const primeiroFato = fatosRelevantesData?.[0]

    return [
        {
            fields: [
                {
                    label: "CETIP",
                    value: primeiroFato?.CETIP ?? characterData?.CETIP ?? "-",
                },
                {
                    label: "ISIN",
                    value: primeiroFato?.ISIN ?? characterData?.ISIN ?? "-",
                },
            ],
        },
        {
            type: "table",
            columns: [
                {
                    label: "Data Referência",
                    key: "dataReferencia",
                    flex: 0.9,
                    minWidth: 130,
                    formatter: formatDate,
                },
                {
                    label: "Data Entrega",
                    key: "dataEntrega",
                    flex: 0.9,
                    minWidth: 130,
                    formatter: formatDate,
                },
                {
                    label: "Categoria",
                    key: "categoria",
                    flex: 1,
                    minWidth: 150,
                },
                {
                    label: "Fato",
                    key: "fato",
                    flex: 3,
                    minWidth: 420,
                    cellSx: {
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                    },
                },
                {
                    label: "Status",
                    key: "status",
                    flex: 0.8,
                    minWidth: 110,
                },
                {
                    label: "Versão",
                    key: "versao",
                    flex: 0.6,
                    minWidth: 90,
                },
                {
                    label: "Link",
                    key: "link",
                    type: "link",
                    flex: 0.8,
                    minWidth: 110,
                },
            ],
            rows: fatosRelevantesData.map((item) => ({
                dataReferencia: item.DataReferencia,
                dataEntrega: item.DataEntrega,
                categoria: item.Categoria,
                fato: item.Fato,
                status: item.Status,
                versao: item.Versao,
                link: item.Link ? "Visualizar" : "-",
                href: item.Link,
            })),
        },
    ]
}

export const getEmissionDetailsModalConfig = ({
    characterData,
    garantiasData,
    fatosRelevantesData,
    offerData,
}) => ({
    guarantees: {
        title: "Garantias",
        sections: getGarantiasModalSections(garantiasData),
    },

    offer_type: {
        title: getOfferModalTitle(offerData),
        empty: shouldShowEmptyOfferModal(offerData),
        emptyText: "Não há dados para o CETIP consultado",
        sections: getOfertaModalSections(offerData),
    },

    relevant_facts_history: {
        title: "Histórico de Fatos Relevantes",
        sections: getFatosRelevantesModalSections(
            fatosRelevantesData,
            characterData
        ),
    },
})